import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { connectSocket, disconnectSocket } from '../utils/socket';
import { IoStatsChartOutline, IoTrophyOutline, IoListOutline, IoTimeOutline, IoHardwareChipOutline, IoPlayOutline, IoStopOutline, IoCheckmarkCircleOutline, IoCloseCircleOutline, IoHomeOutline, IoTvOutline, IoChevronBackOutline, IoChevronForwardOutline, IoCalendarOutline, IoRefreshOutline, IoSearchOutline, IoGameControllerOutline, IoFileTrayOutline } from 'react-icons/io5';
import './QueCards.css';
import { API_URL } from '../utils/networkConfig';

const QueCards = () => {
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [releasingQuestion, setReleasingQuestion] = useState(null);
  const [releasingToHome, setReleasingToHome] = useState(null);
  const [showTimerDialog, setShowTimerDialog] = useState(false);
  const [showHomeTimerDialog, setShowHomeTimerDialog] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [timerMinutes, setTimerMinutes] = useState(5);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [homeTimerMinutes, setHomeTimerMinutes] = useState(5);
  const [homeTimerSeconds, setHomeTimerSeconds] = useState(0);

  // Filter and search state
  const [filterType, setFilterType] = useState('today'); // 'today', 'all'
  const [sortBy, setSortBy] = useState('date_desc'); // 'date_asc', 'date_desc', 'question_asc', 'question_desc'
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const navigate = useNavigate();

  // Calculate pagination values
  const totalPages = Math.ceil(filteredQuestions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentQuestions = filteredQuestions.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, sortBy, searchTerm]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [questions, filterType, sortBy, searchTerm]);

  useEffect(() => {
    fetchQuestions();
    
    // Connect to socket for real-time updates
    const userId = localStorage.getItem('userId');
    if (userId) {
      connectSocket(userId);
    }

    return () => {
      disconnectSocket();
    };
  }, []);

  const applyFiltersAndSort = () => {
    let filtered = [...questions];

    // Apply date filter
    if (filterType === 'today') {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      filtered = filtered.filter(question => {
        const questionDate = new Date(question.created_at).toISOString().split('T')[0];
        return questionDate === todayStr;
      });
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(question =>
        question.question_text.toLowerCase().includes(searchLower) ||
        question.id.toString().includes(searchLower)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'date_desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'question_asc':
          return a.question_text.localeCompare(b.question_text);
        case 'question_desc':
          return b.question_text.localeCompare(a.question_text);
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    setFilteredQuestions(filtered);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Please log in to view questions.');
        return;
      }

      const questionsRes = await fetch(`${API_URL}/api/game-sessions/active-questions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const questionsData = await questionsRes.json();
      console.log('API Response:', questionsData);
      
      if (questionsRes.ok) {
        // Handle both array format and object with questions property
        const questionsList = Array.isArray(questionsData) ? questionsData : (questionsData.questions || []);
        console.log('Questions list:', questionsList);
        const questionsWithDistribution = await Promise.all(
          questionsList.map(async (question) => {
            try {
              const aiGroupingRes = await fetch(`${API_URL}/api/game-sessions/ai-grouping/${question.game_session_id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              
              const rawDistributionRes = await fetch(`${API_URL}/api/game-sessions/answer-distribution/${question.game_session_id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              
              const aiGrouping = aiGroupingRes.ok ? await aiGroupingRes.json() : null;
              const rawDistribution = rawDistributionRes.ok ? await rawDistributionRes.json() : null;
              
              return {
                ...question,
                aiGrouping: aiGrouping?.grouping || [],
                rawDistribution: rawDistribution?.distribution || [],
                // Calculate total answers and total points from AI grouping
                totalAnswers: aiGrouping?.grouping ? aiGrouping.grouping.reduce((total, group) => total + group.totalAnswers, 0) : 0,
                totalPoints: aiGrouping?.grouping ? aiGrouping.grouping.reduce((total, group) => total + (group.totalAnswers * group.pointsPerAnswer), 0) : 0
              };
            } catch (err) {
              console.error(`Error fetching data for question ${question.id}:`, err);
              return question;
            }
          })
        );
        
        setQuestions(questionsWithDistribution);
      } else {
        setError(questionsData.error || 'Failed to fetch questions.');
      }
    } catch (err) {
      console.error('Fetch questions error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReleaseClick = (questionId) => {
    setSelectedQuestionId(questionId);
    setShowTimerDialog(true);
  };

  const handleReleaseQuestion = async (withTimer = false) => {
    if (!selectedQuestionId) return;
    
    setReleasingQuestion(selectedQuestionId);
    setShowTimerDialog(false);
    
    try {
      const token = localStorage.getItem('authToken');
      
      const requestBody = withTimer ? { timerMinutes, timerSeconds } : {};
      
      const response = await fetch(`${API_URL}/api/game-sessions/release-question/${selectedQuestionId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Failed to release question');
      }

      const data = await response.json();

      // Update the question in state
      setQuestions(questions.map(q => 
        q.id === selectedQuestionId 
          ? { 
              ...q, 
              shown_on_air: 1,
              contestantTimer: data.contestantTimer
            }
          : q
      ));

      alert(data.message);
    } catch (error) {
      console.error('Error releasing question:', error);
      setError('Failed to release question: ' + error.message);
    } finally {
      setReleasingQuestion(null);
      setSelectedQuestionId(null);
    }
  };

  const handleReleaseToHome = async (questionId) => {
    setReleasingToHome(questionId);
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/api/game-sessions/release-question-home/${questionId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to release question to home viewers');
      }

      const data = await response.json();

      // Update the question in state
      setQuestions(questions.map(q => 
        q.id === questionId 
          ? { 
              ...q, 
              shown_to_home: 1,
              homeViewerTimer: data.homeViewerTimer
            }
          : q
      ));

      alert(data.message);
    } catch (error) {
      console.error('Error releasing question to home viewers:', error);
      setError('Failed to release question to home viewers: ' + error.message);
    } finally {
      setReleasingToHome(null);
    }
  };

  const handleReleaseToHomeClick = (questionId) => {
    setSelectedQuestionId(questionId);
    setShowHomeTimerDialog(true);
  };

  const handleReleaseToHomeWithTimer = async (withTimer = false) => {
    if (!selectedQuestionId) return;
    
    setReleasingToHome(selectedQuestionId);
    setShowHomeTimerDialog(false);
    
    try {
      const token = localStorage.getItem('authToken');
      
      const requestBody = withTimer ? { timerMinutes: homeTimerMinutes, timerSeconds: homeTimerSeconds } : {};
      
      const response = await fetch(`${API_URL}/api/game-sessions/release-question-home/${selectedQuestionId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Failed to release question to home viewers');
      }

      const data = await response.json();

      // Update the question in state
      setQuestions(questions.map(q => 
        q.id === selectedQuestionId 
          ? { 
              ...q, 
              shown_to_home: 1,
              homeViewerTimer: data.homeViewerTimer
            }
          : q
      ));

      alert(data.message);
    } catch (error) {
      console.error('Error releasing question to home viewers:', error);
      setError('Failed to release question to home viewers: ' + error.message);
    } finally {
      setReleasingToHome(null);
      setSelectedQuestionId(null);
    }
  };

  const handleRevokeQuestion = async (questionId) => {
    setReleasingQuestion(questionId);
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/api/game-sessions/revoke-question/${questionId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to revoke question');
      }

      // Update the question in state
      setQuestions(questions.map(q => 
        q.id === questionId 
          ? { ...q, shown_on_air: 0 }
          : q
      ));

      alert('Question revoked from contestants successfully!');
    } catch (error) {
      console.error('Error revoking question:', error);
      setError('Failed to revoke question: ' + error.message);
    } finally {
      setReleasingQuestion(null);
    }
  };

  const handleRevokeFromHome = async (questionId) => {
    setReleasingToHome(questionId);
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/api/game-sessions/revoke-question-home/${questionId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to revoke question from home viewers');
      }

      // Update the question in state
      setQuestions(questions.map(q => 
        q.id === questionId 
          ? { ...q, shown_to_home: 0 }
          : q
      ));

      alert('Question revoked from home viewers successfully!');
    } catch (error) {
      console.error('Error revoking question from home viewers:', error);
      setError('Failed to revoke question from home viewers: ' + error.message);
    } finally {
      setReleasingToHome(null);
    }
  };

  if (loading) {
    return (
      <div className="que-cards-container">
        <div className="loading-state">
          <IoRefreshOutline className="spin-icon-large" />
          <p>Loading question analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="que-cards-container">
        <div className="error-state">
          <p className="error-message">{error}</p>
          <button onClick={fetchQuestions} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="que-cards-container">
      <div className="page-header">
        <h1>
          <IoStatsChartOutline className="header-icon" />
          Question Analytics Dashboard
        </h1>
        <p className="header-subtitle">
          AI-powered analytics with smart answer grouping (misspellings, synonyms, abbreviations)
        </p>
      </div>

      {/* Filter Controls */}
      <div className="filter-controls">
        <div className="filter-row">
          <div className="filter-group">
            <label className="filter-label"><IoCalendarOutline className="inline-icon" /> Date Filter:</label>
            <select 
              value={filterType} 
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-select"
            >
              <option value="today">Today's Questions Only</option>
              <option value="all">All Questions</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label"><IoRefreshOutline className="inline-icon" /> Sort By:</label>
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="date_desc">Date (Newest First)</option>
              <option value="date_asc">Date (Oldest First)</option>
              <option value="question_asc">Question (A-Z)</option>
              <option value="question_desc">Question (Z-A)</option>
            </select>
          </div>

          <div className="filter-group search-group">
            <label className="filter-label"><IoSearchOutline className="inline-icon" /> Search:</label>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search questions by text or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button onClick={clearSearch} className="search-clear" title="Clear search">
                  ×
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="filter-results">
          <div className="results-info">
            <span className="results-count">
              Showing {filteredQuestions.length > 0 ? `${startIndex + 1}-${Math.min(endIndex, filteredQuestions.length)}` : '0'} of {filteredQuestions.length} questions
              {filterType === 'today' && <span className="filter-indicator"> (Today only)</span>}
              {searchTerm && <span className="search-indicator"> matching "{searchTerm}"</span>}
            </span>
            {filteredQuestions.length > itemsPerPage && (
              <span className="pagination-summary">
                • Page {currentPage} of {totalPages}
              </span>
            )}
          </div>
          
          {filterType === 'today' && filteredQuestions.length === 0 && questions.length > 0 && (
            <div className="no-today-questions">
              <p>No questions were generated today. <button onClick={() => setFilterType('all')} className="show-all-btn">Show all questions</button></p>
            </div>
          )}
        </div>
      </div>

      {/* Timer Dialog */}
      {showTimerDialog && (
        <div className="quecard-modal-overlay">
          <div className="quecard-modal-content">
            <h3>Release Question to Contestants</h3>
            <p>Set a timer to give contestants exclusive access to this question.</p>
            
            <div className="quecard-input-group">
              <label>Timer Duration:</label>
              <div className="quecard-inputs-row">
                <div>
                  <input
                    id="timer-minutes"
                    type="number"
                    min="0"
                    max="60"
                    value={timerMinutes}
                    onChange={(e) => setTimerMinutes(parseInt(e.target.value) || 0)}
                    className="quecard-timer-input"
                    placeholder="0"
                  />
                  <small style={{ display: 'block', textAlign: 'center', marginTop: '0.25rem', color: '#6b7280' }}>
                    minutes
                  </small>
                </div>
                <div>
                  <input
                    id="timer-seconds"
                    type="number"
                    min="0"
                    max="59"
                    value={timerSeconds}
                    onChange={(e) => setTimerSeconds(parseInt(e.target.value) || 0)}
                    className="quecard-timer-input"
                    placeholder="0"
                  />
                  <small style={{ display: 'block', textAlign: 'center', marginTop: '0.25rem', color: '#6b7280' }}>
                    seconds
                  </small>
                </div>
              </div>
            </div>
            
            <div className="quecard-explanation">
              <p><strong>How it works:</strong></p>
              <ul>
                <li>Only contestants (role_id 3) can answer during this timer</li>
                <li>Regular users and admins will be blocked from answering</li>
                <li>Even if the question was expired, it reopens for contestants</li>
              </ul>
            </div>
            
            <div className="quecard-action-buttons">
              <button 
                onClick={() => handleReleaseQuestion(true)} 
                className="quecard-release-timer-btn"
                disabled={releasingQuestion || (timerMinutes === 0 && timerSeconds === 0)}
              >
                {timerMinutes === 0 && timerSeconds === 0 
                  ? 'Set timer duration' 
                  : `Release with ${timerMinutes > 0 ? `${timerMinutes}m ` : ''}${timerSeconds > 0 ? `${timerSeconds}s` : ''} timer`
                }
              </button>
              <button 
                onClick={() => handleReleaseQuestion(false)} 
                className="quecard-release-direct-btn"
                disabled={releasingQuestion}
              >
                Release without timer
              </button>
              <button 
                onClick={() => {
                  setShowTimerDialog(false);
                  setSelectedQuestionId(null);
                }} 
                className="quecard-cancel-btn"
                disabled={releasingQuestion}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Home Timer Dialog */}
      {showHomeTimerDialog && (
        <div className="quecard-modal-overlay">
          <div className="quecard-modal-content">
            <h3>Release Question to Home Viewers</h3>
            <p>Set a timer to give home viewers exclusive access to this question.</p>
            
            <div className="quecard-input-group">
              <label>Timer Duration:</label>
              <div className="quecard-inputs-row">
                <div>
                  <input
                    id="home-timer-minutes"
                    type="number"
                    min="0"
                    max="60"
                    value={homeTimerMinutes}
                    onChange={(e) => setHomeTimerMinutes(parseInt(e.target.value) || 0)}
                    className="quecard-timer-input"
                    placeholder="0"
                  />
                  <small style={{ display: 'block', textAlign: 'center', marginTop: '0.25rem', color: '#6b7280' }}>
                    minutes
                  </small>
                </div>
                <div>
                  <input
                    id="home-timer-seconds"
                    type="number"
                    min="0"
                    max="59"
                    value={homeTimerSeconds}
                    onChange={(e) => setHomeTimerSeconds(parseInt(e.target.value) || 0)}
                    className="quecard-timer-input"
                    placeholder="0"
                  />
                  <small style={{ display: 'block', textAlign: 'center', marginTop: '0.25rem', color: '#6b7280' }}>
                    seconds
                  </small>
                </div>
              </div>
            </div>
            
            <div className="quecard-explanation">
              <p><strong>How it works:</strong></p>
              <ul>
                <li>Only home viewers can answer during this timer</li>
                <li>Regular users and admins will be blocked from answering</li>
                <li>Even if the question was expired, it reopens for home viewers</li>
              </ul>
            </div>
            
            <div className="quecard-action-buttons">
              <button 
                onClick={() => handleReleaseToHomeWithTimer(true)} 
                className="quecard-release-timer-btn"
                disabled={releasingToHome || (homeTimerMinutes === 0 && homeTimerSeconds === 0)}
              >
                {homeTimerMinutes === 0 && homeTimerSeconds === 0 
                  ? 'Set timer duration' 
                  : `Release with ${homeTimerMinutes > 0 ? `${homeTimerMinutes}m ` : ''}${homeTimerSeconds > 0 ? `${homeTimerSeconds}s` : ''} timer`
                }
              </button>
              <button 
                onClick={() => handleReleaseToHomeWithTimer(false)} 
                className="quecard-release-direct-btn"
                disabled={releasingToHome}
              >
                Release without timer
              </button>
              <button 
                onClick={() => {
                  setShowHomeTimerDialog(false);
                  setSelectedQuestionId(null);
                }} 
                className="quecard-cancel-btn"
                disabled={releasingToHome}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {questions.length === 0 ? (
        <div className="empty-state">
          <IoFileTrayOutline className="empty-icon" />
          <h3>No Questions Available</h3>
          <p>Create some questions to see analytics here.</p>
        </div>
      ) : filteredQuestions.length === 0 ? (
        <div className="empty-state">
          <IoFileTrayOutline className="empty-icon" />
          <h3>No Questions Match Your Filters</h3>
          <div className="filter-help">
            {searchTerm ? (
              <p>No questions match your search "{searchTerm}". Try different keywords or <button onClick={clearSearch} className="clear-search-link">clear the search</button>.</p>
            ) : filterType === 'today' ? (
              <p>No questions were generated today. <button onClick={() => setFilterType('all')} className="show-all-btn">Show all questions</button> to see your complete question history.</p>
            ) : (
              <p>Try adjusting your filters to see more results.</p>
            )}
          </div>
        </div>
      ) : (
        <div className="questions-grid">
          {currentQuestions.map((question) => (
            <div key={question.id} className="que-card">
              <div className="que-card-header">
                <div className="question-header-top">
                  <h3 className="question-title">{question.question_text}</h3>
                  {question.aiGrouping.length > 0 && (
                    <div className="ai-badge">
                      <IoHardwareChipOutline className="ai-icon" />
                      <span>AI Grouped</span>
                    </div>
                  )}
                </div>
                <div className="question-meta">
                  <div className="meta-item">
                    <IoTimeOutline className="meta-icon" />
                    <span>Start: {new Date(question.start_time).toLocaleString()}</span>
                  </div>
                  <div className="meta-item">
                    <IoTimeOutline className="meta-icon" />
                    <span>End: {new Date(question.end_time).toLocaleString()}</span>
                  </div>
                  {question.contestantTimer && (
                    <div className="timer-status contestant">
                      <span className="timer-label"><IoGameControllerOutline className="inline-icon" /> Contestant Timer: {question.contestantTimer.durationMinutes} min
                      {question.contestantTimer.durationSeconds > 0 && ` ${question.contestantTimer.durationSeconds} sec`}
                      (ends {new Date(question.contestantTimer.endTime).toLocaleString()})</span>
                    </div>
                  )}
                  
                  {question.homeViewerTimer && (
                    <div className="timer-status home-viewer">
                      <span className="timer-label"><IoHomeOutline className="inline-icon" /> Home Viewer Timer: {question.homeViewerTimer.durationMinutes} min
                      {question.homeViewerTimer.durationSeconds > 0 && ` ${question.homeViewerTimer.durationSeconds} sec`}
                      (ends {new Date(question.homeViewerTimer.endTime).toLocaleString()})</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="que-card-stats">
                <div className="stat-item">
                  <div className="stat-number">{question.totalAnswers}</div>
                  <div className="stat-label">Total Answers</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{question.totalPoints}</div>
                  <div className="stat-label">Total Points</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{question.aiGrouping.length}</div>
                  <div className="stat-label">{question.aiGrouping.length > 0 ? 'AI Groups' : 'Unique Answers'}</div>
                </div>
              </div>

              {/* Release Status Indicators */}
              <div className="release-status">
                <div className="status-row">
                  <div className="status-group">
                    <span className="status-label">Contestants:</span>
                    {question.shown_on_air ? (
                      <div className="status-badge released">
                        <IoCheckmarkCircleOutline className="status-icon" />
                        <span>Released</span>
                      </div>
                    ) : (
                      <div className="status-badge not-released">
                        <IoCloseCircleOutline className="status-icon" />
                        <span>N/R</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="status-group">
                    <span className="status-label">Home Viewers:</span>
                    {question.shown_to_home ? (
                      <div className="status-badge released">
                        <IoCheckmarkCircleOutline className="status-icon" />
                        <span>Released</span>
                      </div>
                    ) : (
                      <div className="status-badge not-released">
                        <IoCloseCircleOutline className="status-icon" />
                        <span>N/R</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="question-actions">
                {/* Contestant Actions */}
                <div className="action-section">
                  <h4 className="actions-title">
                    <IoTvOutline className="action-section-icon" />
                    Contestant Access Control
                  </h4>
                  {releasingQuestion === question.id ? (
                    <div className="loading-action">
                      <IoRefreshOutline className="spin-icon" />
                      <span>Updating...</span>
                    </div>
                  ) : (
                    <div className="action-buttons">
                      {question.shown_on_air ? (
                        <button 
                          onClick={() => handleRevokeQuestion(question.id)} 
                          className="revoke-btn"
                          title="Hide this question from game show contestants"
                        >
                          <IoStopOutline className="action-icon" />
                          Revoke from Contestants
                        </button>
                      ) : (
                        <button 
                          onClick={() => handleReleaseClick(question.id)} 
                          className="release-btn"
                          title="Allow game show contestants to see and answer this question"
                        >
                          <IoPlayOutline className="action-icon" />
                          Allow Contestants to Answer
                        </button>
                      )}
                    </div>
                  )}
                  <p className="action-help">
                    {question.shown_on_air 
                      ? "Game show players can currently see and answer this question."
                      : "Game show players cannot see this question until you release it."
                    }
                  </p>
                </div>

                {/* Home Viewer Actions */}
                <div className="action-section">
                  <h4 className="actions-title">
                    <IoHomeOutline className="action-section-icon" />
                    Home Viewer Access Control
                  </h4>
                  {releasingToHome === question.id ? (
                    <div className="loading-action">
                      <IoRefreshOutline className="spin-icon" />
                      <span>Updating...</span>
                    </div>
                  ) : (
                    <div className="action-buttons">
                      {question.shown_to_home ? (
                        <button 
                          onClick={() => handleRevokeFromHome(question.id)} 
                          className="revoke-btn home-btn"
                          title="Hide this question from people watching at home"
                        >
                          <IoStopOutline className="action-icon" />
                          Revoke from Home Viewers
                        </button>
                      ) : (
                        <button 
                          onClick={() => handleReleaseToHomeClick(question.id)} 
                          className="release-btn home-btn"
                          title="Allow people watching at home to see and answer this question"
                        >
                          <IoPlayOutline className="action-icon" />
                          Show to People at Home
                        </button>
                      )}
                    </div>
                  )}
                  <p className="action-help">
                    {question.shown_to_home 
                      ? "People watching at home can currently see and answer this question."
                      : "People watching at home cannot see this question until you release it."
                    }
                  </p>
                </div>
              </div>

              <div className="answers-section">
                <h4 className="answers-title">
                  <IoTrophyOutline className="answers-icon" />
                  Top {question.aiGrouping.length > 0 ? 'Answer Groups' : 'Answers'}
                </h4>
                {question.aiGrouping.length === 0 ? (
                  <p className="no-answers">No answers submitted yet</p>
                ) : (
                  <div className="answers-list">
                    {question.aiGrouping.map((group, index) => (
                      <div key={index} className={`answer-item rank-${index + 1}`}>
                        <div className="answer-rank">#{index + 1}</div>
                        <div className="answer-content">
                          <div className="answer-text">"{group.representative}"</div>
                          {group.answers.length > 1 && (
                            <div className="grouped-variants">
                              <small>Includes: {group.answers.slice(0, 3).map(a => a.originalText).join(', ')}{group.answers.length > 3 ? '...' : ''}</small>
                            </div>
                          )}
                          <div className="answer-stats">
                            <span className="answer-count">{group.totalAnswers} players</span>
                            <span className="answer-points">{group.pointsPerAnswer} pts each</span>
                            <span className="total-points">{group.totalAnswers * group.pointsPerAnswer} total pts</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
              )}

        {/* Pagination Controls */}
        {filteredQuestions.length > itemsPerPage && (
          <div className="pagination-controls">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              <IoChevronBackOutline className="pagination-icon" />
              Previous
            </button>
            <span className="pagination-info">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
              <IoChevronForwardOutline className="pagination-icon" />
            </button>
          </div>
        )}
    </div>
  );
};

export default QueCards; 